import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Plus, Copy, Power, Users, ShieldCheck, Loader2, MessageSquare, Database, Play, CalendarCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function publicLink(token) {
  return `${window.location.origin}/c/${token}`;
}

export default function GrowthCampaigns() {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "Fortunate Landing Page", default_language: "en" });
  const [reactivating, setReactivating] = useState(false);
  const [reactForm, setReactForm] = useState({ name: "Database Reactivation", product: "Final Expense", batch_size: 25,
    assigned_agent: "", imported_only: true, agency_certification: false, message: "" });
  const [reactDetail, setReactDetail] = useState(null);
  const [reactData, setReactData] = useState(null);
  const [running, setRunning] = useState(false);
  const [leadsFor, setLeadsFor] = useState(null); // campaign obj
  const [leads, setLeads] = useState(null);

  const load = () => api.get("/campaigns").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Campaign name is required"); return; }
    try {
      await api.post("/campaigns", form);
      toast.success("Final Expense campaign created");
      setCreating(false); setForm({ name: "", channel: "Fortunate Landing Page", default_language: "en" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const createReactivation = async () => {
    if (!reactForm.name.trim() || !reactForm.agency_certification) { toast.error("Name the campaign and confirm agency authorization"); return; }
    try {
      const payload = { ...reactForm, batch_size: Number(reactForm.batch_size) };
      if (!payload.message.trim()) delete payload.message;
      const { data } = await api.post("/reactivation-campaigns", payload);
      toast.success(`${data.selection_count} eligible imported lead(s) queued`);
      setReactivating(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openReactivation = async (c) => {
    setReactDetail(c); setReactData(null);
    try { const { data } = await api.get(`/campaigns/${c.id}/reactivation`); setReactData(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setReactData({ members: [], counts: {} }); }
  };

  const runBatch = async (c) => {
    setRunning(true);
    try { const { data } = await api.post(`/campaigns/${c.id}/reactivation/run`);
      if (data.provider_required) toast.error("Campaign is ready, but Twilio/A2P messaging must be connected before texts can send");
      else toast.success(`${data.sent} message(s) sent in this batch`);
      await openReactivation(c); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const toggleBooking = async (c) => {
    try { await api.post(`/campaigns/${c.id}/booking`, { enabled: !c.booking_enabled, post_form_booking: true }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggle = async (c) => {
    try {
      await api.post(`/campaigns/${c.id}/${c.active ? "deactivate" : "activate"}`);
      toast.success(c.active ? "Campaign deactivated" : "Campaign activated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleSmsAck = async (c) => {
    try {
      await api.put(`/campaigns/${c.id}`, { sms_ack_enabled: !(c.sms_ack_enabled !== false) });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copy = (token) => {
    navigator.clipboard.writeText(publicLink(token));
    toast.success("Public landing-page link copied");
  };

  const openLeads = async (c) => {
    setLeadsFor(c); setLeads(null);
    try { const { data } = await api.get(`/campaigns/${c.id}/leads`); setLeads(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setLeads([]); }
  };

  return (
    <div data-testid="growth-campaigns-page">
      <PageHeader title="Growth Engine — Campaigns"
        subtitle="Create and manage agency-owned Final Expense lead-capture campaigns. Prospects submit through a hosted public landing page." />

      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={() => setReactivating(true)} data-testid="gc-reactivate-btn">
          <Database className="mr-2 h-4 w-4" /> Reactivate Imported Database
        </Button>
        <Button className="bg-[#B28A3D] hover:bg-[#9c7833] text-white" onClick={() => setCreating(true)} data-testid="gc-new-btn">
          <Plus className="mr-2 h-4 w-4" /> New Lead-Capture Campaign
        </Button>
      </div>

      {rows === null ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed p-12 text-center text-sm text-muted-foreground" data-testid="gc-empty">
          <Megaphone className="mx-auto mb-3 h-8 w-8 opacity-40" />
          No campaigns yet. Create your first Final Expense campaign to get a public landing-page link.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rows.map((c) => (
            <div key={c.id} className="rounded-xl border bg-card p-5 shadow-sm" data-testid={`gc-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-lg font-bold">{c.name}</h3>
                    <Badge variant={c.active ? "default" : "secondary"} data-testid={`gc-status-${c.id}`}>{c.active ? "Active" : "Inactive"}</Badge>
                    <Badge variant="outline">{c.campaign_type === "database_reactivation" ? "Reactivation" : "New leads"}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{c.product === "final_expense" ? "Final Expense" : c.product} · {c.channel}</div>
                </div>
              </div>

              {c.campaign_type === "database_reactivation" ? (
                <div className="mt-4 grid grid-cols-4 gap-2 rounded-lg bg-muted/50 p-3 text-center text-xs">
                  <div><div className="font-bold text-base">{c.selection_count || 0}</div><div className="text-muted-foreground">Selected</div></div>
                  <div><div className="font-bold text-base">{c.stats?.sent || 0}</div><div className="text-muted-foreground">Sent</div></div>
                  <div><div className="font-bold text-base">{c.stats?.engaged || 0}</div><div className="text-muted-foreground">Engaged</div></div>
                  <div><div className="font-bold text-base">{c.stats?.booked || 0}</div><div className="text-muted-foreground">Booked</div></div>
                </div>
              ) : (
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-muted/50 p-2">
                  <code className="flex-1 truncate text-[11px] text-muted-foreground" data-testid={`gc-link-${c.id}`}>{publicLink(c.public_capture_token)}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(c.public_capture_token)} data-testid={`gc-copy-${c.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => toggle(c)} data-testid={`gc-toggle-${c.id}`}>
                  <Power className="mr-1.5 h-3.5 w-3.5" /> {c.active ? "Deactivate" : "Activate"}
                </Button>
                {c.campaign_type === "database_reactivation" ? <>
                  <Button size="sm" variant="outline" onClick={() => openReactivation(c)}><Users className="mr-1.5 h-3.5 w-3.5" /> Results</Button>
                  <Button size="sm" onClick={() => runBatch(c)} disabled={running}><Play className="mr-1.5 h-3.5 w-3.5" /> Run Next Batch</Button>
                </> : <Button size="sm" variant="outline" onClick={() => openLeads(c)} data-testid={`gc-viewleads-${c.id}`}>
                  <Users className="mr-1.5 h-3.5 w-3.5" /> View Leads
                </Button>}
              </div>

              {c.campaign_type !== "database_reactivation" && <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/50 p-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MessageSquare className="h-3.5 w-3.5" /> Auto SMS acknowledgment
                </div>
                <Switch checked={c.sms_ack_enabled !== false} onCheckedChange={() => toggleSmsAck(c)} data-testid={`gc-smsack-${c.id}`} />
              </div>}
              <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/50 p-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><CalendarCheck className="h-3.5 w-3.5" /> Appointment booking</div>
                <Switch checked={!!c.booking_enabled} onCheckedChange={() => toggleBooking(c)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Database reactivation setup */}
      <Dialog open={reactivating} onOpenChange={setReactivating}>
        <DialogContent className="max-w-xl" data-testid="gc-reactivation-dialog">
          <DialogHeader><DialogTitle>Reactivate an Imported Lead Database</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">Eligible imported leads with a phone number are queued in controlled batches. STOP replies are suppressed automatically; YES replies become engaged leads and receive the campaign booking link.</p>
            <div><Label>Campaign name</Label><Input value={reactForm.name} onChange={(e) => setReactForm((s) => ({ ...s, name: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Product</Label><Input value={reactForm.product} onChange={(e) => setReactForm((s) => ({ ...s, product: e.target.value }))} /></div>
              <div><Label>Batch size</Label><Input type="number" min="1" max="250" value={reactForm.batch_size} onChange={(e) => setReactForm((s) => ({ ...s, batch_size: e.target.value }))} /></div>
            </div>
            <div><Label>Permission-check message</Label><textarea className="min-h-24 w-full rounded-md border bg-background p-3 text-sm" value={reactForm.message} onChange={(e) => setReactForm((s) => ({ ...s, message: e.target.value }))} placeholder="Leave blank to use the agency-branded default with {first_name}." /></div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={reactForm.imported_only} onChange={(e) => setReactForm((s) => ({ ...s, imported_only: e.target.checked }))} /> Use imported leads only</label>
            <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900"><input className="mt-1" type="checkbox" checked={reactForm.agency_certification} onChange={(e) => setReactForm((s) => ({ ...s, agency_certification: e.target.checked }))} />
              <span>I confirm the agency is authorized to contact the selected records and will honor opt-outs. This confirmation is saved with the campaign.</span></label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setReactivating(false)}>Cancel</Button><Button onClick={createReactivation}>Create & Queue Leads</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivation results */}
      <Dialog open={!!reactDetail} onOpenChange={(o) => !o && setReactDetail(null)}>
        <DialogContent className="max-w-4xl" data-testid="gc-reactivation-results">
          <DialogHeader><DialogTitle>Reactivation Results — {reactDetail?.name}</DialogTitle></DialogHeader>
          {!reactData ? <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div> : <>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {["total", "queued", "sent", "engaged", "declined", "opted_out"].map((k) => <div key={k} className="rounded-lg bg-muted p-2 text-center"><div className="font-bold">{reactData.counts?.[k] || 0}</div><div className="text-[10px] capitalize text-muted-foreground">{k.replace("_", " ")}</div></div>)}
            </div>
            <div className="max-h-[52vh] overflow-y-auto rounded-lg border"><table className="w-full text-xs"><thead className="sticky top-0 bg-background"><tr className="border-b text-left"><th className="p-2">Lead</th><th>Status</th><th>Stage</th><th>Assigned</th><th>Attempts</th></tr></thead>
              <tbody>{reactData.members.map((m) => <tr key={m.id} className="border-b"><td className="p-2"><div>{m.lead?.client_name || "Unknown"}</div><div className="text-muted-foreground">{m.lead?.display_id}</div></td><td><Badge variant="secondary">{m.status}</Badge></td><td>{m.lead?.stage || "—"}</td><td>{m.lead?.assigned_agent || "—"}</td><td>{m.attempts || 0}</td></tr>)}</tbody></table></div>
            <div className="flex justify-end"><Button onClick={() => runBatch(reactDetail)} disabled={running}>{running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="mr-1.5 h-4 w-4" />Run Next Batch</>}</Button></div>
          </>}
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="gc-create-dialog">
          <DialogHeader><DialogTitle>New Final Expense Campaign</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Campaign name *</Label><Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Final Expense — Florida Q3" data-testid="gc-name-input" /></div>
            <div><Label>Channel</Label><Input value={form.channel} onChange={(e) => setForm((s) => ({ ...s, channel: e.target.value }))} data-testid="gc-channel-input" /></div>
            <div>
              <Label>Default landing-page language</Label>
              <select value={form.default_language} onChange={(e) => setForm((s) => ({ ...s, default_language: e.target.value }))} className="mt-1 h-9 w-full rounded-md border bg-background px-3 text-sm" data-testid="gc-lang-input">
                <option value="en">English</option>
                <option value="es">Spanish</option>
              </select>
            </div>
            <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              The landing page shows a provisional disclosure. Your agency remains responsible for reviewing and approving its disclosure and consent language before use.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="bg-[#B28A3D] hover:bg-[#9c7833] text-white" onClick={create} data-testid="gc-create-submit">Create Campaign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leads dialog */}
      <Dialog open={!!leadsFor} onOpenChange={(o) => !o && setLeadsFor(null)}>
        <DialogContent className="max-w-4xl" data-testid="gc-leads-dialog">
          <DialogHeader><DialogTitle>Captured leads — {leadsFor?.name}</DialogTitle></DialogHeader>
          {leads === null ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : leads.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="gc-leads-empty">No leads captured yet.</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-background text-left text-muted-foreground">
                  <tr>
                    <th className="p-2">Ref</th><th className="p-2">Name</th><th className="p-2">Contact</th>
                    <th className="p-2">State</th><th className="p-2">Quality</th><th className="p-2">Status</th>
                    <th className="p-2">SMS</th><th className="p-2">Consent</th><th className="p-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t align-top" data-testid={`gc-lead-row-${l.id}`}>
                      <td className="p-2 font-mono">{l.display_id}</td>
                      <td className="p-2">{l.client_name}</td>
                      <td className="p-2">{l.phone}<br /><span className="text-muted-foreground">{l.email}</span></td>
                      <td className="p-2">{l.state}</td>
                      <td className="p-2"><Badge variant="secondary">{l.quality_stage}</Badge></td>
                      <td className="p-2">{l.duplicate_status === "duplicate" ? <Badge variant="destructive">Duplicate</Badge> : <Badge>Unique</Badge>}</td>
                      <td className="p-2">{l.opt_out ? <Badge variant="destructive">Opted out</Badge> : <Badge variant="secondary">{l.sms_ack_status || "—"}</Badge>}</td>
                      <td className="p-2">
                        {l.consent ? (
                          <div className="text-[10px] text-muted-foreground">
                            <div>SMS: {l.consent.sms_permission ? "Yes" : "No"} · Call: {l.consent.call_permission ? "Yes" : "No"}</div>
                            <div>v{l.consent.disclosure_text_version?.slice(-6)} · {l.consent.consent_language}</div>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="p-2 text-[10px] text-muted-foreground">{l.attribution?.utm_source || l.campaign_channel || "direct"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
